/**
 * Typed wrappers for the Hiring (ATS) module API (Phase H.8). Paths and
 * shapes are taken VERBATIM from backend/internal/router/router.go and
 * the hiring.go / hiring_pipeline.go / hiring_offers.go handlers.
 *
 * Cookie-only auth + the CSRF interceptor + 401→refresh are handled by
 * the shared axios instance (lib/api/client.ts). A tenant without the
 * "hiring" feature is fenced server-side by RequireFeature("hiring");
 * the api client turns the backend's 403 { error: "upgrade_required" }
 * into a /pricing redirect, and other 403s reject normally (the caller
 * shows the graceful empty/placeholder state).
 *
 * SECURITY / no-oracle notes (server is the boundary — no client authz):
 *  - Candidate PII + résumé are HR-admin OR assigned-interviewer only.
 *    A forbidden candidate/resume/feedback is a NEUTRAL 404, never a 403
 *    existence oracle — the caller renders "not available", not an
 *    error toast (mirrors the hr_documents.go download idiom).
 *  - The résumé / offer-letter storage KEY is never on the wire; the
 *    bytes only exist behind the authed stream endpoints below
 *    (responseType:"blob" + blobErrorToApi to rehydrate the JSON
 *    envelope so the not_found branch still works).
 *  - The offer state machine is server-enforced AND atomic in SQL; an
 *    illegal decision is 409 `illegal_transition`. The UI only hides a
 *    transition that *would* 409.
 *  - The hire handoff returns the one-time temp password ONCE — there
 *    is NO invite email (the proven P0 add-employee contract).
 */

import { api, blobErrorToApi } from "./client";
import { namedBlob } from "@/lib/download-filename";
import type {
  ApplicationResponse,
  CandidateResponse,
  CareersBrandingResponse,
  ConvertReferralResponse,
  ApprovalDecisionInput,
  CreateOfferInput,
  CreateRequisitionInput,
  EmailTemplateResponse,
  HireApplicantInput,
  HireApplicantResponse,
  HiringEmailSettingsResponse,
  HiringApprovalSettingsResponse,
  HiringAnalytics,
  HiringAnalyticsFilters,
  HiringCostInput,
  HiringCostListFilters,
  HiringCostReport,
  HiringCostReportFilters,
  HiringCostResponse,
  HiringReport,
  HiringReportFilters,
  HiringRequisitionReport,
  ListHiringCostsResponse,
  ImportCandidatesResponse,
  ImportSource,
  InterviewFeedbackResponse,
  InterviewResponse,
  InterviewScheduleFilters,
  ListInterviewScheduleResponse,
  ListCandidateApplicationsResponse,
  ListCandidateMessagesResponse,
  JDTemplateInput,
  JDTemplateResponse,
  ListCandidatesResponse,
  ListEmailTemplatesResponse,
  ListInterviewFeedbackResponse,
  ListMyReferralsResponse,
  ListReferralJobsResponse,
  ListReferralsResponse,
  ListJDTemplatesResponse,
  ListRequisitionsResponse,
  ListScorecardTemplatesResponse,
  ListStagesResponse,
  MoveApplicationInput,
  OfferComponentDefaultsResponse,
  OfferSalaryCalcResponse,
  SalaryCalcRequest,
  OfferDecisionInput,
  OfferResponse,
  PatchInterviewInput,
  PayReferralBonusResponse,
  PendingApprovalsResponse,
  PipelineResponse,
  ReferralAdminResponse,
  RequisitionResponse,
  ScheduleInterviewInput,
  SendCandidateMessageInput,
  SendCandidateMessageResponse,
  ScorecardTemplateInput,
  ScorecardTemplateResponse,
  SubmitFeedbackInput,
  SubmitReferralInput,
  SubmitReferralResponse,
  UpdateCareersBrandingInput,
  UpdateHiringApprovalSettingsInput,
  UpdateOfferInput,
  UpdateEmailTemplateInput,
  UpdateHiringEmailSettingsInput,
  UpdateRequisitionInput,
  ListRequisitionQuestionsResponse,
  RequisitionQuestionResponse,
  RequisitionQuestionInput,
  ListApplicationAnswersResponse,
  CreateSlotOfferInput,
  SlotOfferResponse,
  ListSlotOffersResponse,
  ListMyInterviewsResponse,
  ListApplicationInterviewsResponse,
  PatchCandidateInput,
  EraseCandidateResponse,
  HireDefaultsResponse,
} from "@/types/hiring";

export const HIRING_BASE = "/api/v1/hiring";

// ---------------------------------------------------------------------------
// Requisitions
// ---------------------------------------------------------------------------

// GET /hiring/requisitions[?status=] — any tenant role behind the
// feature gate; role-FILTERED in SQL (manager → own; HR → all). The
// status filter never widens that scope.
export async function listRequisitions(
  params?: { status?: string },
  signal?: AbortSignal,
): Promise<ListRequisitionsResponse> {
  const { data } = await api.get<ListRequisitionsResponse>(
    `${HIRING_BASE}/requisitions`,
    { params, signal },
  );
  return data;
}

// POST /hiring/requisitions — HR-admin + CSRF + Audit server-side.
export async function createRequisition(
  body: CreateRequisitionInput,
): Promise<RequisitionResponse> {
  const { data } = await api.post<RequisitionResponse>(
    `${HIRING_BASE}/requisitions`,
    body,
  );
  return data;
}

// PATCH /hiring/requisitions/{id} — HR-admin OR owning hiring manager
// (server-resolved; a non-owner non-HR caller gets a neutral 404) +
// CSRF + Audit.
export async function patchRequisition(
  id: number,
  body: UpdateRequisitionInput,
): Promise<RequisitionResponse> {
  const { data } = await api.patch<RequisitionResponse>(
    `${HIRING_BASE}/requisitions/${id}`,
    body,
  );
  return data;
}

// GET /hiring/requisitions/{id}/pipeline — HR-admin OR owning hiring
// manager. The board: every tenant stage (ordered) with that
// requisition's applications grouped under their current stage.
export async function getRequisitionPipeline(
  id: number,
  signal?: AbortSignal,
): Promise<PipelineResponse> {
  const { data } = await api.get<PipelineResponse>(
    `${HIRING_BASE}/requisitions/${id}/pipeline`,
    { signal },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Pipeline stages
// ---------------------------------------------------------------------------

// GET /hiring/stages — any tenant role behind the feature gate (the
// board needs the stage list to render).
export async function listStages(
  signal?: AbortSignal,
): Promise<ListStagesResponse> {
  const { data } = await api.get<ListStagesResponse>(
    `${HIRING_BASE}/stages`,
    { signal },
  );
  return data;
}

// ---------------------------------------------------------------------------
// JD templates + scorecard templates (Wave K1.6)
// LIST is any tenant role behind the feature gate (no PII — the stages
// idiom); mutations are HR-admin + CSRF + Audit server-side.
// ---------------------------------------------------------------------------

// GET /hiring/jd-templates — the "start from template" catalogue.
export async function listJDTemplates(
  signal?: AbortSignal,
): Promise<ListJDTemplatesResponse> {
  const { data } = await api.get<ListJDTemplatesResponse>(
    `${HIRING_BASE}/jd-templates`,
    { signal },
  );
  return data;
}

// POST /hiring/jd-templates — HR-admin. Duplicate title → 409
// `title_taken`.
export async function createJDTemplate(
  body: JDTemplateInput,
): Promise<JDTemplateResponse> {
  const { data } = await api.post<JDTemplateResponse>(
    `${HIRING_BASE}/jd-templates`,
    body,
  );
  return data;
}

// PATCH /hiring/jd-templates/{id} — HR-admin; full-replace semantics.
export async function updateJDTemplate(
  id: number,
  body: JDTemplateInput,
): Promise<JDTemplateResponse> {
  const { data } = await api.patch<JDTemplateResponse>(
    `${HIRING_BASE}/jd-templates/${id}`,
    body,
  );
  return data;
}

// DELETE /hiring/jd-templates/{id} — HR-admin. No in-use guard (a JD
// template is a pure starter document; nothing references it).
export async function deleteJDTemplate(id: number): Promise<void> {
  await api.delete(`${HIRING_BASE}/jd-templates/${id}`);
}

// ---------------------------------------------------------------------------
// Screening / knockout questions (Wave K2.4). ALL HR-admin-gated server
// -side (knockout_value is internal). Reorder is via each row's
// sort_order on create/patch — no separate reorder endpoint.
// ---------------------------------------------------------------------------

// GET /hiring/requisitions/{id}/questions — HR-admin; the authored set
// incl. knockout_value, in sort order.
export async function listRequisitionQuestions(
  requisitionId: number,
  signal?: AbortSignal,
): Promise<ListRequisitionQuestionsResponse> {
  const { data } = await api.get<ListRequisitionQuestionsResponse>(
    `${HIRING_BASE}/requisitions/${requisitionId}/questions`,
    { signal },
  );
  return data;
}

// POST /hiring/requisitions/{id}/questions — HR-admin. 422 on a bad
// shape (text question with a knockout_value; number/yes_no value that
// won't parse) or the 20-question cap.
export async function createRequisitionQuestion(
  requisitionId: number,
  body: RequisitionQuestionInput,
): Promise<RequisitionQuestionResponse> {
  const { data } = await api.post<RequisitionQuestionResponse>(
    `${HIRING_BASE}/requisitions/${requisitionId}/questions`,
    body,
  );
  return data;
}

// PATCH /hiring/questions/{id} — HR-admin; full-replace semantics.
export async function updateRequisitionQuestion(
  id: number,
  body: RequisitionQuestionInput,
): Promise<RequisitionQuestionResponse> {
  const { data } = await api.patch<RequisitionQuestionResponse>(
    `${HIRING_BASE}/questions/${id}`,
    body,
  );
  return data;
}

// DELETE /hiring/questions/{id} — HR-admin. Cascades any stored answers.
export async function deleteRequisitionQuestion(id: number): Promise<void> {
  await api.delete(`${HIRING_BASE}/questions/${id}`);
}

// GET /hiring/applications/{id}/answers — HR-admin OR owning hiring
// manager; the stored screening answers (prompt+kind+answer; never the
// knockout_value).
export async function listApplicationAnswers(
  applicationId: number,
  signal?: AbortSignal,
): Promise<ListApplicationAnswersResponse> {
  const { data } = await api.get<ListApplicationAnswersResponse>(
    `${HIRING_BASE}/applications/${applicationId}/answers`,
    { signal },
  );
  return data;
}

// GET /hiring/scorecard-templates — the schedule-dialog picker AND the
// feedback form's label source (looked up by
// interview.scorecard_template_id).
export async function listScorecardTemplates(
  signal?: AbortSignal,
): Promise<ListScorecardTemplatesResponse> {
  const { data } = await api.get<ListScorecardTemplatesResponse>(
    `${HIRING_BASE}/scorecard-templates`,
    { signal },
  );
  return data;
}

// POST /hiring/scorecard-templates — HR-admin. Attribute-shape problems
// are 422 (slug keys, unique, 1..20); duplicate name → 409 `name_taken`.
export async function createScorecardTemplate(
  body: ScorecardTemplateInput,
): Promise<ScorecardTemplateResponse> {
  const { data } = await api.post<ScorecardTemplateResponse>(
    `${HIRING_BASE}/scorecard-templates`,
    body,
  );
  return data;
}

// PATCH /hiring/scorecard-templates/{id} — HR-admin; full-replace.
// Existing feedback rows keep their stored {key: score} maps —
// removing/renaming a key only affects NEW submissions.
export async function updateScorecardTemplate(
  id: number,
  body: ScorecardTemplateInput,
): Promise<ScorecardTemplateResponse> {
  const { data } = await api.patch<ScorecardTemplateResponse>(
    `${HIRING_BASE}/scorecard-templates/${id}`,
    body,
  );
  return data;
}

// DELETE /hiring/scorecard-templates/{id} — HR-admin. A template
// referenced by ≥1 interview is a 409 `template_in_use` (the
// pipeline-stages in-use idiom) — the caller surfaces the message.
export async function deleteScorecardTemplate(id: number): Promise<void> {
  await api.delete(`${HIRING_BASE}/scorecard-templates/${id}`);
}

// ---------------------------------------------------------------------------
// Candidates (HR-admin; PII)
// ---------------------------------------------------------------------------

// GET /hiring/candidates[?source=] — HR-admin only (candidate PII).
export async function listCandidates(
  params?: { source?: string },
  signal?: AbortSignal,
): Promise<ListCandidatesResponse> {
  const { data } = await api.get<ListCandidatesResponse>(
    `${HIRING_BASE}/candidates`,
    { params, signal },
  );
  return data;
}

// GET /hiring/candidates/{id} — HR-admin OR an interviewer assigned to
// one of the candidate's applications. A forbidden/unknown id is a
// NEUTRAL 404 (no existence oracle) — the caller treats it as "not
// available".
export async function getCandidate(
  id: number,
  signal?: AbortSignal,
): Promise<CandidateResponse> {
  const { data } = await api.get<CandidateResponse>(
    `${HIRING_BASE}/candidates/${id}`,
    { signal },
  );
  return data;
}

// POST /hiring/candidates — HR-admin + CSRF + Audit. Manual add via
// multipart (optional résumé). Per-tenant email dedupe: re-adding an
// existing email returns the existing candidate (200, deduplicated:true)
// rather than erroring. The browser sets the multipart Content-Type
// (boundary) — pass FormData + an undefined Content-Type so axios drops
// its JSON default (the hr_documents.go upload idiom).
export async function createCandidate(input: {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  notes?: string;
  resume?: File;
}): Promise<CandidateResponse> {
  const fd = new FormData();
  fd.append("first_name", input.firstName);
  fd.append("last_name", input.lastName);
  fd.append("email", input.email);
  if (input.phone) fd.append("phone", input.phone);
  if (input.notes) fd.append("notes", input.notes);
  if (input.resume) fd.append("resume", input.resume);
  const { data } = await api.post<CandidateResponse>(
    `${HIRING_BASE}/candidates`,
    fd,
    { headers: { "Content-Type": undefined } },
  );
  return data;
}

// POST /hiring/candidates/import — HR-admin + CSRF + Audit. §4a
// recruiter-driven sourced import. SINGLE (first_name/last_name/email/
// phone + optional resume) OR BULK (one or more `resumes` file parts
// and/or a `csv` file part). `source` ∈ linkedin|naukri|other;
// optional source_url + requisition_id (attaches every imported
// candidate to that req at the Sourced stage). Returns the per-row
// results contract { results:[...], summary:{...} }.
export async function importCandidates(input: {
  source: ImportSource;
  sourceUrl?: string;
  requisitionId?: number;
  // SINGLE
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  resume?: File;
  // BULK
  resumes?: File[];
  csv?: File;
}): Promise<ImportCandidatesResponse> {
  const fd = new FormData();
  fd.append("source", input.source);
  if (input.sourceUrl) fd.append("source_url", input.sourceUrl);
  if (input.requisitionId != null) {
    fd.append("requisition_id", String(input.requisitionId));
  }
  if (input.firstName) fd.append("first_name", input.firstName);
  if (input.lastName) fd.append("last_name", input.lastName);
  if (input.email) fd.append("email", input.email);
  if (input.phone) fd.append("phone", input.phone);
  if (input.resume) fd.append("resume", input.resume);
  for (const f of input.resumes ?? []) fd.append("resumes", f);
  if (input.csv) fd.append("csv", input.csv);
  const { data } = await api.post<ImportCandidatesResponse>(
    `${HIRING_BASE}/candidates/import`,
    fd,
    { headers: { "Content-Type": undefined } },
  );
  return data;
}

// GET /hiring/candidates/{id}/resume — HR-admin OR an interviewer
// assigned to one of the candidate's applications. Streamed; the
// storage key is never on the wire. A forbidden/missing résumé is a
// NEUTRAL 404 (no oracle) — rehydrate the blob error envelope so the
// caller's not_found branch works.
export async function downloadResume(id: number): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${HIRING_BASE}/candidates/${id}/resume`,
      { responseType: "blob" },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

// ---------------------------------------------------------------------------
// Applications / pipeline (HR-admin OR owning hiring manager)
// ---------------------------------------------------------------------------

// POST /hiring/applications/{id}/move — change stage_id. Moving onto a
// rejected-terminal stage REQUIRES reject_reason (the backend 400s
// `reject_reason_required` otherwise) and sets status='rejected'.
export async function moveApplication(
  id: number,
  body: MoveApplicationInput,
): Promise<ApplicationResponse> {
  const { data } = await api.post<ApplicationResponse>(
    `${HIRING_BASE}/applications/${id}/move`,
    body,
  );
  return data;
}

// POST /hiring/applications/{id}/withdraw — marks an active application
// withdrawn.
export async function withdrawApplication(
  id: number,
): Promise<ApplicationResponse> {
  const { data } = await api.post<ApplicationResponse>(
    `${HIRING_BASE}/applications/${id}/withdraw`,
    {},
  );
  return data;
}

// ---------------------------------------------------------------------------
// Interviews
// ---------------------------------------------------------------------------

// POST /hiring/applications/{id}/interviews — HR-admin OR owning hiring
// manager. Schedule a round + assign an interviewer (any tenant user,
// proven in-tenant) → notifies the interviewer.
export async function scheduleInterview(
  applicationId: number,
  body: ScheduleInterviewInput,
): Promise<InterviewResponse> {
  const { data } = await api.post<InterviewResponse>(
    `${HIRING_BASE}/applications/${applicationId}/interviews`,
    body,
  );
  return data;
}

// PATCH /hiring/interviews/{id} — HR-admin OR owning hiring manager OR
// the assigned interviewer. Status transition.
export async function patchInterview(
  id: number,
  body: PatchInterviewInput,
): Promise<InterviewResponse> {
  const { data } = await api.patch<InterviewResponse>(
    `${HIRING_BASE}/interviews/${id}`,
    body,
  );
  return data;
}

// GET /hiring/interviews/mine — ANY tenant role. The caller's own
// assigned interviews (tenant- AND interviewer-scoped in SQL).
export async function listMyInterviews(
  signal?: AbortSignal,
): Promise<ListMyInterviewsResponse> {
  const { data } = await api.get<ListMyInterviewsResponse>(
    `${HIRING_BASE}/interviews/mine`,
    { signal },
  );
  return data;
}

// GET /hiring/interviews — the company-wide schedule (HR: every round; a
// manager: rounds on their own jobs; anyone else 403). Paged.
export async function listInterviewSchedule(
  filters: InterviewScheduleFilters,
  signal?: AbortSignal,
): Promise<ListInterviewScheduleResponse> {
  const params: Record<string, string> = { when: filters.when };
  if (filters.requisition_id) params.requisition_id = String(filters.requisition_id);
  if (filters.interviewer_id) params.interviewer_id = String(filters.interviewer_id);
  if (filters.page && filters.page > 1) params.page = String(filters.page);
  if (filters.per_page) params.per_page = String(filters.per_page);
  const { data } = await api.get<ListInterviewScheduleResponse>(
    `${HIRING_BASE}/interviews`,
    { params, signal },
  );
  return data;
}

// GET /hiring/interviews/{id}/feedback — visibility-scoped. A caller
// with NO read relationship gets a NEUTRAL 404 (no existence oracle) —
// the caller treats that as "no feedback visible", not an error.
export async function listInterviewFeedback(
  interviewId: number,
  signal?: AbortSignal,
): Promise<ListInterviewFeedbackResponse> {
  const { data } = await api.get<ListInterviewFeedbackResponse>(
    `${HIRING_BASE}/interviews/${interviewId}/feedback`,
    { signal },
  );
  return data;
}

// POST /hiring/interviews/{id}/feedback — ONLY the assigned interviewer
// (anyone else gets a neutral 404). One row per (interview,
// interviewer); a re-submit updates in place.
export async function submitInterviewFeedback(
  interviewId: number,
  body: SubmitFeedbackInput,
): Promise<InterviewFeedbackResponse> {
  const { data } = await api.post<InterviewFeedbackResponse>(
    `${HIRING_BASE}/interviews/${interviewId}/feedback`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Offers (HR-admin; GET-for-application is HR-admin OR owning mgr)
// ---------------------------------------------------------------------------

// GET /hiring/applications/{id}/offer — the (at most one) offer for an
// application. K1.7 / D-4: an AUTHORIZED caller with no offer yet gets
// a 204 No Content → returned as `null` ("no offer yet", devtools-clean,
// not an error). A forbidden caller still gets the NEUTRAL 404.
export async function getOfferForApplication(
  applicationId: number,
  signal?: AbortSignal,
): Promise<OfferResponse | null> {
  const res = await api.get<OfferResponse>(
    `${HIRING_BASE}/applications/${applicationId}/offer`,
    { signal },
  );
  if (res.status === 204) return null;
  return res.data;
}

// POST /hiring/applications/{id}/offer — HR-admin + CSRF + Audit. Draft
// an offer (one active offer per application → 409 `offer_exists` on a
// duplicate). ctc_paise is INTEGER paise (>= 0).
export async function createOffer(
  applicationId: number,
  body: CreateOfferInput,
): Promise<OfferResponse> {
  const { data } = await api.post<OfferResponse>(
    `${HIRING_BASE}/applications/${applicationId}/offer`,
    body,
  );
  return data;
}

// PATCH /hiring/offers/{id} — HR-admin + CSRF + Audit (K1.1). Edit a
// DRAFT offer's ctc / joining date / component breakup. The server
// merges with the current row and re-validates the merged state: a sum
// mismatch is 422 `ctc_component_mismatch` with per-component deltas;
// a non-draft offer is 409 `not_editable` (it is a frozen document).
export async function updateOffer(
  id: number,
  body: UpdateOfferInput,
): Promise<OfferResponse> {
  const { data } = await api.patch<OfferResponse>(
    `${HIRING_BASE}/offers/${id}`,
    body,
  );
  return data;
}

// GET /hiring/offer-component-defaults?ctc_paise= — HR-admin (K1.1).
// The G5-statutory-math suggested split (12% employer PF on basic
// capped at the PF ceiling in force today — ₹25,000/mo from 17 Sep
// 2026). Correct by construction: the suggestion's annualized sum
// equals ctc_paise exactly, so submitting
// it unmodified always passes the server's 422 gate.
export async function getOfferComponentDefaults(
  ctcPaise: number,
  signal?: AbortSignal,
): Promise<OfferComponentDefaultsResponse> {
  const { data } = await api.get<OfferComponentDefaultsResponse>(
    `${HIRING_BASE}/offer-component-defaults`,
    { params: { ctc_paise: ctcPaise }, signal },
  );
  return data;
}

// POST /hiring/salary-calculate — the offer salary calculator v2
// (HR-admin + CSRF; pure maths, nothing stored): the one request shape with
// the extras (shift allowance, annual bonus, insurance, NPS, one-time
// payments). Answers the breakup plus the matching offer components (they
// add up to ctc_paise — the total CTC — exactly; one-time rows ride along).
export async function postSalaryCalc(
  body: SalaryCalcRequest,
  signal?: AbortSignal,
): Promise<OfferSalaryCalcResponse> {
  const { data } = await api.post<OfferSalaryCalcResponse>(
    `${HIRING_BASE}/salary-calculate`,
    body,
    { signal },
  );
  return data;
}

// POST /hiring/applications/{id}/offer-preview — HR-admin + CSRF. The
// letter PDF for a draft's unsaved figures; nothing is stored.
export async function previewOfferLetter(
  applicationId: number,
  body: CreateOfferInput,
): Promise<Blob> {
  try {
    const { data } = await api.post<Blob>(
      `${HIRING_BASE}/applications/${applicationId}/offer-preview`,
      body,
      { responseType: "blob" },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

// POST /hiring/offers/{id}/send — HR-admin + CSRF + Audit. Renders +
// stores the letter PDF server-side, flips draft→sent. Only a draft is
// sendable (else 409 `not_sendable`).
export async function sendOffer(
  id: number,
): Promise<OfferResponse & { email_status?: string }> {
  // email_status (wave B): "sent" | "skipped" (email isn't set up) |
  // "failed" | "suppressed" (no consent) | "off" (automatic email off).
  const { data } = await api.post<OfferResponse & { email_status?: string }>(
    `${HIRING_BASE}/offers/${id}/send`,
    {},
  );
  return data;
}

// POST /hiring/offers/{id}/decision — HR-admin + CSRF + Audit. Move to
// accepted|declined|revoked. The legal edges are server-enforced; an
// illegal one is 409 `illegal_transition` (the UI only offers legal
// transitions — see canDecideOffer in lib/hiring-format.ts).
export async function decideOffer(
  id: number,
  body: OfferDecisionInput,
): Promise<OfferResponse> {
  const { data } = await api.post<OfferResponse>(
    `${HIRING_BASE}/offers/${id}/decision`,
    body,
  );
  return data;
}

// K1.5 — GET /hiring/offers/{id}/accept-link. HR-admin only. Returns the
// candidate's public e-accept URL to copy + share (e.g. over WhatsApp),
// alongside the automated offer-sent email. 409 `no_active_link` when the
// offer is not a still-acceptable 'sent' offer.
export interface OfferAcceptLinkResponse {
  accept_link: string;
  expires_at?: string;
}

export async function getOfferAcceptLink(
  id: number,
): Promise<OfferAcceptLinkResponse> {
  const { data } = await api.get<OfferAcceptLinkResponse>(
    `${HIRING_BASE}/offers/${id}/accept-link`,
  );
  return data;
}

// K2.3 — POST /hiring/applications/{id}/status-link. HR-admin or owning
// hiring manager. Mints (or refreshes) a tokenized public status link for
// the candidate. Returns the full public URL — the token is embedded in the
// URL, never returned separately. Calling this again replaces the old link
// ("refresh link" affordance). CSRF + Audit middleware enforced server-side.
export interface ApplicationStatusLinkResponse {
  /** Full public URL — e.g. https://app.workright.in/status/{uuid}. */
  status_url: string;
}

export async function mintApplicationStatusLink(
  applicationId: number,
): Promise<ApplicationStatusLinkResponse> {
  const { data } = await api.post<ApplicationStatusLinkResponse>(
    `${HIRING_BASE}/applications/${applicationId}/status-link`,
  );
  return data;
}

// GET /hiring/offers/{id}/letter — HR-admin only. Streams the stored
// offer-letter PDF (the storage key is never on the wire). 404 cleanly
// if not sent yet / blob gone — rehydrate the blob error envelope.
export async function downloadOfferLetter(id: number): Promise<Blob> {
  try {
    const { data } = await api.get<Blob>(
      `${HIRING_BASE}/offers/${id}/letter`,
      { responseType: "blob" },
    );
    return data;
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

// ---------------------------------------------------------------------------
// Hire → Onboard handoff (company_admin / hr_manager)
// ---------------------------------------------------------------------------

// POST /hiring/applications/{id}/hire — the differentiator. In ONE
// atomic transaction: create a loginable employee + flip the
// application → hired + auto-assign onboarding. Returns the one-time
// temp password ONCE (NO invite email — the P0 contract) and
// onboarding_skipped + a warning when no active template exists.
// Errors propagate as the backend's { error, message, field } envelope
// (email_taken / no_consent / not_hireable / invalid_role / …).
// GET /hiring/applications/{id}/hire-defaults — HR-admin. What the hire
// form starts from (accepted offer's joining date + the requisition).
export async function getHireDefaults(
  applicationId: number,
  signal?: AbortSignal,
): Promise<HireDefaultsResponse> {
  const { data } = await api.get<HireDefaultsResponse>(
    `${HIRING_BASE}/applications/${applicationId}/hire-defaults`,
    { signal },
  );
  return data;
}

// PATCH /hiring/candidates/{id} — HR-admin + CSRF + Audit. Notes, phone,
// current / expected CTC and notice period after creation.
export async function patchCandidate(
  id: number,
  body: PatchCandidateInput,
): Promise<CandidateResponse> {
  const { data } = await api.patch<CandidateResponse>(
    `${HIRING_BASE}/candidates/${id}`,
    body,
  );
  return data;
}

// DELETE /hiring/candidates/{id} — HR-admin + CSRF + Audit. Permanent
// erasure (DPDP); a hired candidate is refused with 409 candidate_hired.
export async function eraseCandidate(
  id: number,
): Promise<EraseCandidateResponse> {
  const { data } = await api.delete<EraseCandidateResponse>(
    `${HIRING_BASE}/candidates/${id}`,
  );
  return data;
}

export async function hireApplicant(
  applicationId: number,
  body: HireApplicantInput,
): Promise<HireApplicantResponse> {
  const { data } = await api.post<HireApplicantResponse>(
    `${HIRING_BASE}/applications/${applicationId}/hire`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Reports (H.10a) — HR-admin only
// ---------------------------------------------------------------------------

// GET /hiring/reports/summary — funnel/source/time-to-hire aggregate for
// the caller's tenant. RequireFeature("hiring") +
// RequireRole(company_admin, hr_manager) server-side; the page hard
// role-gates so this is never fired for a non-admin (a 403 here would
// be defence-in-depth only). No candidate PII on the wire — every row
// is either a count or an id/name/department the HR admin already sees
// on the requisition/pipeline surfaces.
//
// K1.7: optional filters (from/to date range replacing the 30d/90d
// defaults; requisition/manager/department scope). Empty values are
// stripped so an unfiltered call stays the byte-stable v1 request.
export async function getReportsSummary(
  filters?: HiringReportFilters,
  signal?: AbortSignal,
): Promise<HiringReport> {
  const params: Record<string, string | number> = {};
  if (filters?.from) params.from = filters.from;
  if (filters?.to) params.to = filters.to;
  if (filters?.requisition_id) params.requisition_id = filters.requisition_id;
  if (filters?.hiring_manager_id) {
    params.hiring_manager_id = filters.hiring_manager_id;
  }
  if (filters?.department) params.department = filters.department;
  const { data } = await api.get<HiringReport>(
    `${HIRING_BASE}/reports/summary`,
    { params, signal },
  );
  return data;
}

// GET /hiring/reports/requisitions/{id} — K1.7 per-requisition
// drill-down: the summary shape filtered to one req PLUS the
// per-applicant roster. HR-admin OR the owning hiring manager
// (server-enforced; anyone else gets a neutral 404).
export async function getRequisitionReport(
  id: number,
  signal?: AbortSignal,
): Promise<HiringRequisitionReport> {
  const { data } = await api.get<HiringRequisitionReport>(
    `${HIRING_BASE}/reports/requisitions/${id}`,
    { signal },
  );
  return data;
}

// ---------------------------------------------------------------------------
// K1.7 closeouts (H.8)
// ---------------------------------------------------------------------------

// GET /hiring/candidates/{id}/applications — the per-candidate
// application list (replaces the ?application=<id> context hack as the
// primary source; the search-param stays a fallback). Same visibility
// as the candidate profile: HR-admin OR an assigned interviewer; a
// forbidden/unknown id is a NEUTRAL 404.
export async function listCandidateApplications(
  candidateId: number,
  signal?: AbortSignal,
): Promise<ListCandidateApplicationsResponse> {
  const { data } = await api.get<ListCandidateApplicationsResponse>(
    `${HIRING_BASE}/candidates/${candidateId}/applications`,
    { signal },
  );
  return data;
}

// GET /hiring/applications/{id}/interviews — every interview round on
// an application. HR-admin OR the owning hiring manager; anyone else
// (incl. an assigned interviewer, who has /interviews/mine) gets a
// neutral 404.
export async function listApplicationInterviews(
  applicationId: number,
  signal?: AbortSignal,
): Promise<ListApplicationInterviewsResponse> {
  const { data } = await api.get<ListApplicationInterviewsResponse>(
    `${HIRING_BASE}/applications/${applicationId}/interviews`,
    { signal },
  );
  return data;
}

// ---------------------------------------------------------------------------
// K1.3 — Candidate email comms (templates / settings / timeline / send)
// ---------------------------------------------------------------------------

// GET /hiring/email-templates — HR-admin. The backend lazily ensures
// the 6 system defaults, so this is never empty for a healthy tenant.
export async function listEmailTemplates(
  signal?: AbortSignal,
): Promise<ListEmailTemplatesResponse> {
  const { data } = await api.get<ListEmailTemplatesResponse>(
    `${HIRING_BASE}/email-templates`,
    { signal },
  );
  return data;
}

// PATCH /hiring/email-templates/{id} — HR-admin + CSRF + Audit. Partial
// edit of name/subject/body_text/is_active; kind is immutable.
export async function updateEmailTemplate(
  id: number,
  body: UpdateEmailTemplateInput,
): Promise<EmailTemplateResponse> {
  const { data } = await api.patch<EmailTemplateResponse>(
    `${HIRING_BASE}/email-templates/${id}`,
    body,
  );
  return data;
}

// GET /hiring/email-settings — HR-admin. Automated-trigger toggles.
export async function getEmailSettings(
  signal?: AbortSignal,
): Promise<HiringEmailSettingsResponse> {
  const { data } = await api.get<HiringEmailSettingsResponse>(
    `${HIRING_BASE}/email-settings`,
    { signal },
  );
  return data;
}

// PATCH /hiring/email-settings — HR-admin + CSRF + Audit.
export async function updateEmailSettings(
  body: UpdateHiringEmailSettingsInput,
): Promise<HiringEmailSettingsResponse> {
  const { data } = await api.patch<HiringEmailSettingsResponse>(
    `${HIRING_BASE}/email-settings`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Careers-page branding (Wave K2.5)
// ---------------------------------------------------------------------------

// GET /hiring/careers-branding — HR-admin. Accent colour + blurb +
// has_logo (the logo bytes are served separately).
export async function getCareersBranding(
  signal?: AbortSignal,
): Promise<CareersBrandingResponse> {
  const { data } = await api.get<CareersBrandingResponse>(
    `${HIRING_BASE}/careers-branding`,
    { signal },
  );
  return data;
}

// PATCH /hiring/careers-branding — HR-admin + CSRF + Audit. accent_color
// must be #RRGGBB-or-empty; blurb is length-capped server-side. A field
// set to "" CLEARS it.
export async function updateCareersBranding(
  body: UpdateCareersBrandingInput,
): Promise<CareersBrandingResponse> {
  const { data } = await api.patch<CareersBrandingResponse>(
    `${HIRING_BASE}/careers-branding`,
    body,
  );
  return data;
}

// POST /hiring/careers-branding/logo — HR-admin + CSRF + Audit. Multipart
// (field "logo"): a PNG/JPEG/WebP image, server-capped at 2 MiB and
// type-sniffed. Pass FormData + an undefined Content-Type so axios drops
// its JSON default (the createCandidate idiom).
export async function uploadCareersLogo(
  logo: File,
): Promise<CareersBrandingResponse> {
  const fd = new FormData();
  fd.append("logo", logo);
  const { data } = await api.post<CareersBrandingResponse>(
    `${HIRING_BASE}/careers-branding/logo`,
    fd,
    { headers: { "Content-Type": undefined } },
  );
  return data;
}

// DELETE /hiring/careers-branding/logo — HR-admin + CSRF + Audit.
// Idempotent: removing an absent logo is a clean 200.
export async function deleteCareersLogo(): Promise<CareersBrandingResponse> {
  const { data } = await api.delete<CareersBrandingResponse>(
    `${HIRING_BASE}/careers-branding/logo`,
  );
  return data;
}

// GET /hiring/candidates/{id}/messages — HR-admin OR the hiring manager
// of one of the candidate's applications. Anyone else gets a NEUTRAL
// 404 (no existence oracle) — the caller treats it as "not available",
// never an error toast.
export async function listCandidateMessages(
  candidateId: number,
  signal?: AbortSignal,
): Promise<ListCandidateMessagesResponse> {
  const { data } = await api.get<ListCandidateMessagesResponse>(
    `${HIRING_BASE}/candidates/${candidateId}/messages`,
    { signal },
  );
  return data;
}

// POST /hiring/candidates/{id}/messages — HR-admin + CSRF + Audit.
// Template-prefilled or free-form; {{vars}} substituted SERVER-side.
// A no-consent candidate is NOT an error: the backend records a
// 'suppressed' row and the 201 response carries suppressed/note.
export async function sendCandidateMessage(
  candidateId: number,
  body: SendCandidateMessageInput,
): Promise<SendCandidateMessageResponse> {
  const { data } = await api.post<SendCandidateMessageResponse>(
    `${HIRING_BASE}/candidates/${candidateId}/messages`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Referrals (Wave K1.4)
// ---------------------------------------------------------------------------

// GET /hiring/referrals/jobs — ANY tenant role behind the feature gate.
// The minimal open-req projection for "Refer someone" (no pipeline
// telemetry — the backend only serializes title/dept/location/type/bonus).
export async function listReferralJobs(
  signal?: AbortSignal,
): Promise<ListReferralJobsResponse> {
  const { data } = await api.get<ListReferralJobsResponse>(
    `${HIRING_BASE}/referrals/jobs`,
    { signal },
  );
  return data;
}

// POST /hiring/referrals — ANY tenant role + CSRF + Audit. Multipart
// (optional résumé, server-capped at 5 MiB, sniffed PDF/DOC/DOCX). Pass
// FormData + an undefined Content-Type so axios drops its JSON default
// (the createCandidate idiom above).
export async function submitReferral(
  input: SubmitReferralInput,
): Promise<SubmitReferralResponse> {
  const fd = new FormData();
  fd.append("requisition_id", String(input.requisitionId));
  fd.append("first_name", input.firstName);
  fd.append("last_name", input.lastName);
  fd.append("email", input.email);
  if (input.phone) fd.append("phone", input.phone);
  if (input.note) fd.append("note", input.note);
  if (input.resume) fd.append("resume", input.resume);
  const { data } = await api.post<SubmitReferralResponse>(
    `${HIRING_BASE}/referrals`,
    fd,
    { headers: { "Content-Type": undefined } },
  );
  return data;
}

// GET /hiring/referrals/mine — the caller's OWN referrals, COARSE
// status only (the backend never serializes pipeline fields here).
export async function listMyReferrals(
  signal?: AbortSignal,
): Promise<ListMyReferralsResponse> {
  const { data } = await api.get<ListMyReferralsResponse>(
    `${HIRING_BASE}/referrals/mine`,
    { signal },
  );
  return data;
}

// GET /hiring/referrals[?status=] — HR-admin inbox.
export async function listReferrals(
  params?: { status?: string },
  signal?: AbortSignal,
): Promise<ListReferralsResponse> {
  const { data } = await api.get<ListReferralsResponse>(
    `${HIRING_BASE}/referrals`,
    { params, signal },
  );
  return data;
}

// GET /hiring/referrals/{id}/resume — HR-admin; streamed, key never on
// the wire. Neutral 404 on missing/forbidden (blob envelope rehydrated).
export async function downloadReferralResume(id: number): Promise<Blob> {
  try {
    const res = await api.get<Blob>(
      `${HIRING_BASE}/referrals/${id}/resume`,
      { responseType: "blob" },
    );
    return namedBlob(res.data, res.headers?.["content-disposition"]);
  } catch (err) {
    throw await blobErrorToApi(err);
  }
}

// POST /hiring/referrals/{id}/convert — HR-admin + CSRF + Audit. Creates
// the candidate (source='referral', referred_by_id set, résumé key
// transferred) or dedupe-LINKS an existing one (deduplicated: true —
// HR-only knowledge; the referrer always just sees 'converted').
export async function convertReferral(
  id: number,
): Promise<ConvertReferralResponse> {
  const { data } = await api.post<ConvertReferralResponse>(
    `${HIRING_BASE}/referrals/${id}/convert`,
  );
  return data;
}

// POST /hiring/referrals/{id}/reject — HR-admin + CSRF + Audit.
export async function rejectReferral(
  id: number,
): Promise<ReferralAdminResponse> {
  const { data } = await api.post<ReferralAdminResponse>(
    `${HIRING_BASE}/referrals/${id}/reject`,
  );
  return data;
}

// POST /hiring/referrals/{id}/bonus/pay — HR-admin + CSRF + Audit. Lands
// the pending bonus as a payroll-override BONUS EARNING for the referrer
// on the open payroll run; 409 no_open_run when no draft run exists
// (the caller surfaces "create the draft run first").
export async function payReferralBonus(
  id: number,
): Promise<PayReferralBonusResponse> {
  const { data } = await api.post<PayReferralBonusResponse>(
    `${HIRING_BASE}/referrals/${id}/bonus/pay`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// K1.2 — Requisition + offer approval chains (migration 067)
// ---------------------------------------------------------------------------

// GET /hiring/approval-settings — HR-admin. The per-tenant gate toggles.
export async function getApprovalSettings(
  signal?: AbortSignal,
): Promise<HiringApprovalSettingsResponse> {
  const { data } = await api.get<HiringApprovalSettingsResponse>(
    `${HIRING_BASE}/approval-settings`,
    { signal },
  );
  return data;
}

// PATCH /hiring/approval-settings — HR-admin + CSRF + Audit.
export async function updateApprovalSettings(
  body: UpdateHiringApprovalSettingsInput,
): Promise<HiringApprovalSettingsResponse> {
  const { data } = await api.patch<HiringApprovalSettingsResponse>(
    `${HIRING_BASE}/approval-settings`,
    body,
  );
  return data;
}

// POST /hiring/requisitions/{id}/submit-approval — owner-OR-HR + CSRF +
// Audit. Moves the requisition into the approval queue (→ pending).
export async function submitRequisitionApproval(
  id: number,
): Promise<RequisitionResponse> {
  const { data } = await api.post<RequisitionResponse>(
    `${HIRING_BASE}/requisitions/${id}/submit-approval`,
    {},
  );
  return data;
}

// POST /hiring/requisitions/{id}/approval-decision — the tenant's
// configured approver_role ONLY, and NEVER the submitter (403
// cannot_self_approve). CSRF + Audit.
export async function decideRequisitionApproval(
  id: number,
  body: ApprovalDecisionInput,
): Promise<RequisitionResponse> {
  const { data } = await api.post<RequisitionResponse>(
    `${HIRING_BASE}/requisitions/${id}/approval-decision`,
    body,
  );
  return data;
}

// POST /hiring/offers/{id}/submit-approval — HR-admin + CSRF + Audit.
export async function submitOfferApproval(
  id: number,
): Promise<OfferResponse> {
  const { data } = await api.post<OfferResponse>(
    `${HIRING_BASE}/offers/${id}/submit-approval`,
    {},
  );
  return data;
}

// POST /hiring/offers/{id}/approval-decision — the tenant's configured
// approver_role ONLY, never the offer's creator. CSRF + Audit.
export async function decideOfferApproval(
  id: number,
  body: ApprovalDecisionInput,
): Promise<OfferResponse> {
  const { data } = await api.post<OfferResponse>(
    `${HIRING_BASE}/offers/${id}/approval-decision`,
    body,
  );
  return data;
}

// GET /hiring/approvals/pending[?exclude_self=1] — the tenant's
// configured approver_role ONLY. The combined approver inbox (pending
// requisitions + offers). `exclude_self=1` removes the caller's own
// submissions (a submitter can't self-approve).
export async function listPendingApprovals(
  signal?: AbortSignal,
  params?: { exclude_self?: boolean },
): Promise<PendingApprovalsResponse> {
  const { data } = await api.get<PendingApprovalsResponse>(
    `${HIRING_BASE}/approvals/pending`,
    {
      params: params?.exclude_self ? { exclude_self: 1 } : undefined,
      signal,
    },
  );
  return data;
}

// ---------------------------------------------------------------------------
// K5.2 — Hiring cost ledger + cost-per-hire (migration 074). ALL HR-admin
// server-side (hiringAdminRole). amount_paise is INTEGER paise on the
// wire; system rows (source != manual) are read-only (PATCH/DELETE → 409
// system_owned). Empty filter values are stripped.
// ---------------------------------------------------------------------------

// GET /hiring/costs[?from&to&requisition_id&category] — the cost ledger.
export async function listHiringCosts(
  filters?: HiringCostListFilters,
  signal?: AbortSignal,
): Promise<ListHiringCostsResponse> {
  const params: Record<string, string | number> = {};
  if (filters?.from) params.from = filters.from;
  if (filters?.to) params.to = filters.to;
  if (filters?.requisition_id) params.requisition_id = filters.requisition_id;
  if (filters?.category) params.category = filters.category;
  const { data } = await api.get<ListHiringCostsResponse>(
    `${HIRING_BASE}/costs`,
    { params, signal },
  );
  return data;
}

// POST /hiring/costs — HR-admin + CSRF + Audit. Records a MANUAL cost.
export async function createHiringCost(
  body: HiringCostInput,
): Promise<HiringCostResponse> {
  const { data } = await api.post<HiringCostResponse>(
    `${HIRING_BASE}/costs`,
    body,
  );
  return data;
}

// PATCH /hiring/costs/{id} — HR-admin + CSRF + Audit. Manual rows only; a
// system-owned row is 409 `system_owned`.
export async function updateHiringCost(
  id: number,
  body: HiringCostInput,
): Promise<HiringCostResponse> {
  const { data } = await api.patch<HiringCostResponse>(
    `${HIRING_BASE}/costs/${id}`,
    body,
  );
  return data;
}

// DELETE /hiring/costs/{id} — HR-admin + CSRF + Audit. Manual rows only;
// a system-owned row is 409 `system_owned`.
export async function deleteHiringCost(id: number): Promise<void> {
  await api.delete(`${HIRING_BASE}/costs/${id}`);
}

// GET /hiring/costs/report[?from&to&requisition_id] — the cost-per-hire
// bundle (total / by-category / by-req / per-source ROI / monthly trend).
export async function getHiringCostReport(
  filters?: HiringCostReportFilters,
  signal?: AbortSignal,
): Promise<HiringCostReport> {
  const params: Record<string, string | number> = {};
  if (filters?.from) params.from = filters.from;
  if (filters?.to) params.to = filters.to;
  if (filters?.requisition_id) params.requisition_id = filters.requisition_id;
  const { data } = await api.get<HiringCostReport>(
    `${HIRING_BASE}/costs/report`,
    { params, signal },
  );
  return data;
}

// GET /hiring/reports/analytics[?from&to&department&hiring_manager_id&
// requisition_id] — Wave K5.1 deep hiring-analytics aggregate (stage-event
// funnel + time-in-stage + time-to-fill/hire + offer/source/recruiter/
// interviewer/req-aging/renege/quality-of-hire panels). RequireFeature
// ("hiring") + RequireRole(company_admin, hr_manager) server-side; the page
// hard role-gates so this is never fired for a non-admin. No candidate PII
// on the wire — counts + ratios + stage/source labels + manager/interviewer
// ids the HR admin already sees on the directory/pipeline surfaces.
export async function getAnalytics(
  filters?: HiringAnalyticsFilters,
  signal?: AbortSignal,
): Promise<HiringAnalytics> {
  const params: Record<string, string | number> = {};
  if (filters?.from) params.from = filters.from;
  if (filters?.to) params.to = filters.to;
  if (filters?.requisition_id) params.requisition_id = filters.requisition_id;
  if (filters?.hiring_manager_id) {
    params.hiring_manager_id = filters.hiring_manager_id;
  }
  if (filters?.department) params.department = filters.department;
  const { data } = await api.get<HiringAnalytics>(
    `${HIRING_BASE}/reports/analytics`,
    { params, signal },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Wave K2.2 — candidate self-scheduling: interview slot offers.
// ---------------------------------------------------------------------------

// POST /hiring/applications/{id}/slot-offers — HR-admin OR owning hiring
// manager + CSRF + Audit. Mints a crypto/rand UUID token + emails the
// candidate the pick link (DPDP consent-gated). The response carries the
// slot offer id + status; the token is NEVER on the wire.
export async function createSlotOffer(
  applicationId: number,
  body: CreateSlotOfferInput,
): Promise<SlotOfferResponse> {
  const { data } = await api.post<SlotOfferResponse>(
    `${HIRING_BASE}/applications/${applicationId}/slot-offers`,
    body,
  );
  return data;
}

// GET /hiring/applications/{id}/slot-offers — HR-admin OR owning hiring
// manager. All slot offers for an application, newest-first.
export async function listSlotOffers(
  applicationId: number,
  signal?: AbortSignal,
): Promise<ListSlotOffersResponse> {
  const { data } = await api.get<ListSlotOffersResponse>(
    `${HIRING_BASE}/applications/${applicationId}/slot-offers`,
    { signal },
  );
  return data;
}

// POST /hiring/applications/{id}/slot-offers/{offerID}/cancel — HR-admin
// OR owning hiring manager + CSRF + Audit. Only 'open' offers.
export async function cancelSlotOffer(
  applicationId: number,
  offerID: number,
): Promise<SlotOfferResponse> {
  const { data } = await api.post<SlotOfferResponse>(
    `${HIRING_BASE}/applications/${applicationId}/slot-offers/${offerID}/cancel`,
    {},
  );
  return data;
}
